import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { XCircle, Printer, Send, ExternalLink, Receipt, BellRing, CheckCircle2, RotateCcw, FileDown, Layers } from "lucide-react";
import { eur, downloadBlob } from "@/lib/workflow";
import { PaidDialog } from "@/components/PaidDialog";
import { StornoDialog } from "@/components/StornoDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { YearOverview } from "@/components/YearOverview";

const KIND = { auftragsbestaetigung: "Auftragsbestätigung", rechnung: "Rechnung", mahnung: "Mahnung" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "–");
const today = () => new Date().toISOString().slice(0, 10);
const CLOSED = ["bezahlt", "storniert"];
const isOverdue = (d) => d.type === "rechnung" && !CLOSED.includes(d.status) && (d.due || d.date || "").slice(0, 10) < today();

async function openBlob(url, params) {
  const w = window.open("", "_blank");
  try {
    const r = await api.get(url, { params, responseType: "blob" });
    const href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (w) w.location.href = href;
    else window.open(href, "_blank");
  } catch (e) {
    if (w) w.close();
    toast.error(e.response?.status === 404 ? "Keine Dokumente vorhanden" : "PDF konnte nicht geöffnet werden");
  }
}

function Billable({ rows, can, onDone }) {
  const [busy, setBusy] = useState("");
  if (!rows.length) return null;
  const create = async (r, send) => {
    setBusy(r.customer_id + r.issue_id);
    try {
      const { data } = await api.post("/documents/invoice", { issue_id: r.issue_id, customer_id: r.customer_id, send_mail: send });
      toast.success(`Rechnung ${data.number} erstellt${send ? " und versendet" : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="billable-list">
      <div className="font-semibold text-amber-900 mb-2 flex items-center gap-2"><Receipt size={16} /> Noch nicht abgerechnet ({rows.length})</div>
      <div className="divide-y divide-amber-200">
        {rows.map((r) => (
          <div key={r.customer_id + r.issue_id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`billable-${r.customer_id}`}>
            <div className="flex-1 min-w-[220px]"><b>{r.customer_name}</b> · {r.issue_name} · {r.format} <span className="text-slate-500">({r.order_no})</span></div>
            <div className="font-semibold">{eur(r.net)} netto</div>
            {can.rechnung && <>
              <Button size="sm" disabled={!!busy} onClick={() => create(r, false)} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid={`billable-create-${r.customer_id}`}>Rechnung erstellen</Button>
              {can.mail && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => create(r, true)} data-testid={`billable-create-send-${r.customer_id}`}>Erstellen & senden</Button>}
            </>}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusCell({ d }) {
  if (d.type === "mahnung") return <span className="text-xs text-slate-600">Frist {fmt(d.deadline)}</span>;
  if (d.type === "storno") return <span className="text-xs text-slate-600" data-testid={`doc-status-${d.id}`}>storniert {d.invoice_no}</span>;
  if (d.status === "storniert") return <div className="text-xs text-slate-500" data-testid={`doc-status-${d.id}`}><b>storniert</b> am {fmt(d.cancelled_at)}<div className="text-[11px]">{d.storno_no}{d.cancel_reason ? ` · ${d.cancel_reason}` : ""}</div></div>;
  if (d.type !== "rechnung") return <span className="text-xs text-slate-400">–</span>;
  const s = d.dunning;
  return (
    <div>
      {d.status === "bezahlt"
        ? <span className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-100 text-emerald-700" data-testid={`doc-status-${d.id}`}>bezahlt am {fmt(d.paid_at)}</span>
        : isOverdue(d)
          ? <span className="text-xs text-red-700 font-semibold whitespace-nowrap" data-testid={`doc-status-${d.id}`}>im Verzug seit {fmt(d.due || d.date)}</span>
          : <span className="text-[11px] font-semibold px-2 py-1 rounded-md bg-amber-100 text-amber-700" data-testid={`doc-status-${d.id}`}>offen</span>}
      {d.status === "bezahlt" && d.paid_bank && <div className="text-[11px] text-slate-500 mt-1">{d.paid_bank}</div>}
      {s && (s.next
        ? <div className={`text-[11px] mt-1 ${s.is_due ? "text-red-700 font-semibold" : "text-slate-500"}`} data-testid="doc-dunning-hint">{s.level > 0 && `Mahnstufe ${s.level} · `}{s.next_name} {s.is_due ? `fällig seit ${fmt(s.due_on)}` : `ab ${fmt(s.due_on)}`}</div>
        : <div className="text-[11px] text-red-700 mt-1">alle 3 Mahnstufen erstellt</div>)}
    </div>
  );
}

function Actions({ d, can, onSend, onPaid, onReopen, onDunning, onStorno }) {
  const P = ({ icon: I, label, className = "", ...p }) => <Button size="sm" variant="outline" className={`h-8 px-3 text-xs ${className}`} {...p}><I size={14} className="mr-1.5" />{label}</Button>;
  const S = ({ icon: I, label, ...p }) => <button type="button" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 disabled:opacity-40 disabled:hover:text-slate-500" {...p}><I size={13} />{label}</button>;
  const inv = d.type === "rechnung";
  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex flex-nowrap justify-end gap-1.5">
        {can.zahlung && inv && !CLOSED.includes(d.status) && <P icon={CheckCircle2} label="Als bezahlt markieren" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onPaid(d)} data-testid={`doc-paid-${d.id}`} />}
        {can.zahlung && inv && d.status === "bezahlt" && <P icon={RotateCcw} label="Wieder offen" onClick={() => onReopen(d)} data-testid={`doc-reopen-${d.id}`} />}
        {can.mahnung && d.dunning?.next && <P icon={BellRing} label={`${d.dunning.next_name.split(" (")[0]} erstellen`} className={d.dunning.is_due ? "border-red-300 text-red-700 hover:bg-red-50" : ""} onClick={() => onDunning(d)} data-testid={`doc-dunning-${d.id}`} />}
      </div>
      <div className="flex items-center gap-4 whitespace-nowrap">
        {can.drucken && <S icon={Printer} label="Drucken" title="PDF herunterladen und im Acrobat Reader drucken" onClick={() => { downloadBlob(`/documents/${d.id}/file`, {}, d.filename); toast.success("PDF wird heruntergeladen – bitte im Acrobat Reader öffnen und drucken"); }} data-testid={`doc-print-${d.id}`} />}
        {can.mail && <S icon={Send} label="Senden" disabled={!d.email} title={d.email ? `An ${d.email}` : "Keine E-Mail-Adresse"} onClick={() => onSend(d)} data-testid={`doc-send-${d.id}`} />}
        {d.drive_link && <a href={d.drive_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid={`doc-drive-${d.id}`}><ExternalLink size={13} />Drive</a>}
        {can.storno && inv && d.status !== "storniert" && <button type="button" className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800" onClick={() => onStorno(d)} data-testid={`doc-storno-${d.id}`}><XCircle size={13} />Stornieren</button>}
      </div>
    </div>
  );
}

function Row(props) {
  const { d } = props;
  return (
    <tr className={`align-top ${d.status === "storniert" ? "opacity-60" : ""} ${isOverdue(d) ? "bg-red-50/70 hover:bg-red-50 shadow-[inset_4px_0_0_#dc2626]" : "hover:bg-slate-50"}`} data-testid={`doc-row-${d.id}`} data-overdue={isOverdue(d) ? "true" : undefined}>
      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmt(d.date || d.created_at)}</td>
      <td className="px-4 py-3">{props.can.drucken ? <button type="button" title="PDF herunterladen" onClick={() => downloadBlob(`/documents/${d.id}/file`, {}, d.filename)} className="font-semibold text-slate-900 hover:underline whitespace-nowrap text-left" data-testid={`doc-number-${d.id}`}>{d.number}</button> : <span className="font-semibold text-slate-900 whitespace-nowrap" data-testid={`doc-number-${d.id}`}>{d.number}</span>}<div className="text-xs text-slate-500">{d.type === "mahnung" ? `${d.dunning_name} zu ${d.invoice_no}` : d.type === "storno" ? `Stornorechnung zu ${d.invoice_no}` : KIND[d.type]}{d.zugferd ? " · E-Rechnung" : ""}</div></td>
      <td className="px-4 py-3"><Link to={`/anzeigenkunden/anfragen/${d.customer_id}?issue=${d.issue_id}`} className="hover:underline">{d.customer_name}</Link><div className="text-xs text-slate-500">{d.issue_name}</div></td>
      <td className="px-4 py-3 text-right whitespace-nowrap">{eur(d.net)}<div className="text-xs text-slate-500">brutto {eur(d.gross)}</div></td>
      <td className="px-4 py-3"><StatusCell d={d} /><div className="text-[11px] text-slate-500 mt-1">{d.sent_at ? `gesendet ${fmt(d.sent_at)}` : "nicht gesendet"}</div></td>
      <td className="px-4 py-3"><Actions {...props} /></td>
    </tr>
  );
}

function Filters({ kind, setKind, issue, setIssue, status, setStatus, issues }) {
  const pill = (active) => `px-3 py-1.5 rounded-md text-sm border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`;
  return (
    <div className="flex flex-wrap gap-2">
      {[["", "Alle"], ["auftragsbestaetigung", "Auftragsbestätigungen"], ["rechnung", "Rechnungen"], ["mahnung", "Mahnungen"], ["storno", "Stornos"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setKind(k)} className={pill(kind === k)} data-testid={`doc-filter-${k || "all"}`}>{l}</button>
      ))}
      <select value={issue} onChange={(e) => setIssue(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="doc-filter-issue">
        <option value="">Alle Ausgaben</option>
        {issues.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
      </select>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="doc-filter-status">
        <option value="">Jeder Status</option>
        <option value="offen">Rechnungen offen</option>
        <option value="faellig">Rechnungen bereits fällig</option>
        <option value="bezahlt">Rechnungen bezahlt</option>
        <option value="storniert">Rechnungen storniert</option>
      </select>
    </div>
  );
}

export default function Accounting() {
  const { user } = useAuth();
  const p = user?.permissions || {};
  const admin = user?.role === "admin";
  const edit = admin || p.buchhaltung === "edit";
  const flag = (k) => admin || (p[k] === "edit" && (k === "bh_drucken" || edit));
  const can = { edit, drucken: flag("bh_drucken"), rechnung: flag("bh_rechnung"), zahlung: flag("bh_zahlung"), mahnung: flag("bh_mahnung"), storno: flag("bh_storno"), mail: !!user?.is_chef };
  const [docs, setDocs] = useState(null);
  const [billable, setBillable] = useState([]);
  const [kind, setKind] = useState("");
  const [issue, setIssue] = useState("");
  const [status, setStatus] = useState("");
  const [paying, setPaying] = useState(null);
  const [storno, setStorno] = useState(null);
  const [tab, setTab] = useState("docs");
  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
    api.get("/documents/billable").then((r) => setBillable(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const issues = useMemo(() => [...new Map((docs || []).map((d) => [d.issue_id, d.issue_name])).entries()], [docs]);
  const shown = (docs || []).filter((d) => (!kind || d.type === kind) && (!issue || d.issue_id === issue)
    && (!status || (d.type === "rechnung" && (CLOSED.includes(status) ? d.status === status : !CLOSED.includes(d.status) && (status === "offen" || isOverdue(d))))));
  const send = async (d) => {
    if (!window.confirm(`${KIND[d.type]} ${d.number} erneut an ${d.email} senden?`)) return;
    try { await api.post(`/documents/${d.id}/send`); toast.success("Versendet"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const dunning = async (d) => {
    const s = d.dunning;
    if (!window.confirm(`${s.next_name} zur Rechnung ${d.number} erstellen?${s.is_due ? "" : `\n\nHinweis: Laut Einstellungen ist diese Stufe erst ab ${fmt(s.due_on)} vorgesehen.`}`)) return;
    const sendNow = can.mail && !!d.email && window.confirm(`Mahnung direkt per Mail an ${d.email} senden?`);
    try {
      const { data } = await api.post(`/documents/${d.id}/dunning`, { send_mail: sendNow });
      toast.success(`${data.dunning_name} erstellt${sendNow ? " und versendet" : ""}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reopen = async (d) => {
    if (!window.confirm(`Rechnung ${d.number} wieder auf „offen“ setzen?`)) return;
    try { await api.put(`/documents/${d.id}/status`, { status: "offen" }); toast.success("Wieder offen"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const issueName = issues.find(([id]) => id === issue)?.[1];
  const invoiceIssues = [...new Map((docs || []).filter((d) => d.type === "rechnung").map((d) => [d.issue_id, d.issue_name])).entries()];
  const [pick, setPick] = useState(false);
  const batchPrint = () => {
    if (!invoiceIssues.length) { toast.error("Es gibt noch keine Rechnungen für den Sammeldruck"); return; }
    const target = issue || (invoiceIssues.length === 1 ? invoiceIssues[0][0] : "");
    if (target) batchDownload(target);
    else setPick(true);
  };
  const batchDownload = (id) => {
    const n = invoiceIssues.find(([i]) => i === id)?.[1] || "";
    downloadBlob("/documents/batch.pdf", { issue_id: id }, `Sammeldruck Rechnungen ${n}.pdf`);
    toast.success("PDF wird heruntergeladen – bitte im Acrobat Reader öffnen und drucken");
  };
  return (
    <div className="p-6 lg:p-8" data-testid="accounting-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Buchhaltung</h1>
          <p className="text-slate-500 mt-1">Auftragsbestätigungen, Rechnungen und Mahnungen ansehen, drucken und versenden.</p>
        </div>
        {tab === "docs" && can.drucken && <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => openBlob("/documents/report.pdf", { type: kind, issue_id: issue, status }, false)} data-testid="doc-report-pdf"><FileDown size={16} className="mr-1.5" /> Auswertung als PDF</Button>
          <Button variant="outline" onClick={batchPrint} data-testid="doc-batch-print"><Layers size={16} className="mr-1.5" /> Sammeldruck Rechnungen (PDF){issueName ? ` – ${issueName}` : ""}</Button>
        </div>}
      </div>
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {[["docs", "Dokumente"], ["overview", "Jahresübersicht"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`accounting-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "overview" ? <YearOverview openPdf={can.drucken ? openBlob : null} /> : <>
      <Billable rows={billable} can={can} onDone={load} />
      <div className="mb-4"><Filters {...{ kind, setKind, issue, setIssue, status, setStatus, issues }} /></div>
      {!issue && invoiceIssues.length > 1 && <p className="text-xs text-slate-500 mb-3">Tipp: Beim Sammeldruck wählst Du die Ausgabe aus – oder filterst sie vorher oben.</p>}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-4 py-3">Datum</th><th className="px-4 py-3">Nummer</th><th className="px-4 py-3">Kunde / Ausgabe</th><th className="px-4 py-3 text-right">Netto</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aktionen</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((d) => <Row key={d.id} d={d} can={can} onSend={send} onPaid={setPaying} onReopen={reopen} onDunning={dunning} onStorno={setStorno} />)}
          </tbody>
        </table>
        {docs && !shown.length && <div className="p-8 text-center text-slate-400" data-testid="doc-empty">Keine Dokumente für diesen Filter.</div>}
      </div>
      </>}
      <Dialog open={pick} onOpenChange={setPick}>
        <DialogContent data-testid="batch-pick-dialog">
          <DialogHeader><DialogTitle>Sammeldruck – Ausgabe wählen</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {invoiceIssues.map(([id, n]) => (
              <Button key={id} variant="outline" className="w-full justify-start" onClick={() => { setPick(false); batchDownload(id); }} data-testid={`batch-pick-${id}`}>
                <Layers size={16} className="mr-2" /> {n} ({(docs || []).filter((d) => d.type === "rechnung" && d.issue_id === id).length} Rechnungen)
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <StornoDialog doc={storno} onClose={() => setStorno(null)} onDone={() => { setStorno(null); load(); }} />
      <PaidDialog doc={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />
    </div>
  );
}
